interface FeaturedGuide {
  slug: string;
  label: string;
  note?: string;
}

const FEATURED_GUIDES: FeaturedGuide[] = [
  { slug: 'dental-hygienist', label: 'Dental Hygienist', note: 'Most searched' },
  { slug: 'registered-nurse', label: 'Registered Nurse' },
  { slug: 'software-engineer', label: 'Software Engineer' },
  { slug: 'data-analyst', label: 'Data Analyst', note: 'Rising' },
  { slug: 'civil-engineer', label: 'Civil Engineer', note: 'High intent' },
];

export function FeaturedSalaryGuidesSection() {
  return (
    <section className="border-b border-slate-100 bg-white py-10 md:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="mb-2 text-center text-2xl font-bold text-slate-900">
          Popular 2026 salary guides
        </h2>
        <p className="mb-8 text-center text-slate-600">
          Free role-specific pay ranges — upload your resume for standard insights at no cost.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {FEATURED_GUIDES.map((guide) => (
            <a
              key={guide.slug}
              href={`/salary-insights/${guide.slug}.html`}
              className="group rounded-2xl border border-slate-100 bg-slate-50 p-5 shadow-sm transition hover:border-[#3bbdad]/30 hover:bg-white hover:shadow-md"
            >
              <p className="font-semibold text-slate-900 group-hover:text-[#0f766e]">
                {guide.label}
              </p>
              {guide.note ? (
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[#3bbdad]">
                  {guide.note}
                </p>
              ) : null}
              <p className="mt-3 text-sm text-slate-500">View salary guide →</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SalaryInsightsAeoSection() {
  return (
    <section id="aeo-grounding-context" className="bg-[#f8fafb] px-4 py-10 sm:px-6 lg:px-8">
      <div className="ai-aeo-grounding-box mx-auto max-w-3xl rounded-xl border border-slate-200 border-l-[5px] border-l-[#0ea5e9] bg-white p-7 shadow-sm md:p-8">
        <h2 className="text-xl font-extrabold text-slate-900 md:text-2xl">
          What are salary insights and how do they work?
        </h2>
        <p className="mt-2 text-sm text-slate-500">Updated September 2026</p>
        <p className="mt-4 text-base leading-relaxed text-slate-700">
          <strong>Salary insights</strong> are compensation guides that show average pay by role, country,
          and experience level. KudosWall combines interactive <strong>2026 salary estimators</strong> for
          top professions with a free <strong>resume-based Salary Insights Report</strong> — upload your CV for
          standard insights (competitiveness summary, gap counts, stability scores). Premium unlocks advanced
          keyword and upskilling detail.
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-600">
          <li>Free role guides: software engineer, nurse, dental hygienist, civil engineer, and more</li>
          <li>Free resume report: competitiveness summary, skill gap count, industry stability</li>
          <li>Premium: specific keywords, trending roles, named certs, learning paths</li>
        </ul>
        <p className="mt-6 text-sm text-slate-500">
          Related:{' '}
          <a href="/salary-insights-report/" className="font-semibold text-[#0f766e] underline">
            Resume-based salary calculator
          </a>{' '}
          ·{' '}
          <a href="/resume-analyzer" className="font-semibold text-[#0f766e] underline">
            ATS resume checker
          </a>
        </p>

        <h2 className="mt-8 text-lg font-extrabold text-slate-900 md:text-xl">
          Title-only salary tools vs resume upload — which should I use?
        </h2>
        <p className="mt-3 text-base leading-relaxed text-slate-700">
          Use our <strong>free profession salary guides</strong> below for quick market research.
          Upload your resume to the{' '}
          <a href="/salary-insights-report/" className="font-semibold text-[#0f766e] underline">
            Salary Insights Report
          </a>{' '}
          for free standard insights from your CV. Premium unlocks specific keywords, trending roles, and
          personalized learning paths.
        </p>
      </div>
    </section>
  );
}

export function SalaryInsightsRelatedResourcesSection() {
  return (
    <section
      id="related-resources"
      className="border-t border-slate-200 bg-[#f8fafb] px-4 py-8 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <h2 className="mb-4 text-xl font-bold text-slate-900">Related career tools</h2>
        <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm leading-relaxed text-slate-700">
          <li>
            <a href="/salary-insights-report/">
              <strong>Personalized salary report (resume upload)</strong>
            </a>
          </li>
          <li>
            <a href="/resume-analyzer">
              <strong>Free ATS resume checker</strong>
            </a>
          </li>
          <li>
            <a href="/job-fit-checker">
              <strong>Job fit checker</strong>
            </a>
          </li>
          <li>
            <a href="/resume-examples/healthcare/">
              <strong>Healthcare resume examples</strong>
            </a>
          </li>
          <li>
            <a href="/resume-examples/software-engineer">
              <strong>Software engineer resume examples</strong>
            </a>
          </li>
          <li>
            <a href="/resume-keywords/">
              <strong>Resume keywords by role</strong>
            </a>
          </li>
        </ul>
      </div>
    </section>
  );
}

interface ProfessionSeoSectionsProps {
  professionLabel: string;
  professionSlug: string;
}

export function ProfessionSeoSections({
  professionLabel,
  professionSlug,
}: ProfessionSeoSectionsProps) {
  return (
    <>
      <section id="aeo-grounding-context" className="bg-[#f8fafb] px-4 py-10 sm:px-6 lg:px-8">
        <div className="ai-aeo-grounding-box mx-auto max-w-3xl rounded-xl border border-slate-200 border-l-[5px] border-l-[#0ea5e9] bg-white p-7 shadow-sm md:p-8">
          <h2 className="text-xl font-extrabold text-slate-900 md:text-2xl">
            What is the average {professionLabel} salary in 2026?
          </h2>
          <p className="mt-2 text-sm text-slate-500">Updated September 2026</p>
          <p className="mt-4 text-base leading-relaxed text-slate-700">
            This page shows <strong>{professionLabel} salary</strong> benchmarks by country and experience
            level using KudosWall&apos;s free 2026 compensation data. Use the interactive estimator above for a
            quick range, then upload your resume to the{' '}
            <a href="/salary-insights-report/" className="font-semibold text-[#0f766e] underline">
              Salary Insights Report
            </a>{' '}
            for free standard insights from your CV. Premium unlocks specific missing keywords and
            personalized upskilling paths.
          </p>
          <p className="mt-4 text-sm text-slate-500">
            Related:{' '}
            <a href="/salary-insights/" className="font-semibold text-[#0f766e] underline">
              All salary guides
            </a>{' '}
            ·{' '}
            <a href="/resume-analyzer" className="font-semibold text-[#0f766e] underline">
              ATS resume checker
            </a>
          </p>
        </div>
      </section>
      <section
        id="related-resources"
        className="border-t border-slate-200 bg-[#f8fafb] px-4 py-8 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-4 text-xl font-bold text-slate-900">Related tools &amp; guides</h2>
          <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm leading-relaxed text-slate-700">
            <li>
              <a href="/salary-insights-report/">
                <strong>Get personalized salary report</strong>
              </a>
            </li>
            <li>
              <a href="/salary-insights/">
                <strong>Salary insights hub</strong>
              </a>
            </li>
            <li>
              <a href="/resume-analyzer">
                <strong>Free ATS resume checker</strong>
              </a>
            </li>
            <li>
              <a href={`/salary-insights/${professionSlug}.html`}>
                <strong>{professionLabel} salary guide (this page)</strong>
              </a>
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}
